import * as React from "react";

/**
 * Heading levels from context (redesign phase 6, #455, consistency finding
 * #2): a part that renders a heading takes its level from where it sits, so a
 * page reads h1 → h2 → h3 without each caller counting.
 *
 *   PageLayout   its content starts at 2 (the PageHeader, or the hidden
 *                `title` of a workspace, is the h1)
 *   AddinShell   the same: hidden h1 from `title`, content at 2
 *   Pane         its title at the context level, everything inside one deeper
 *   Card         the same: CardTitle at the context level, the rest one deeper
 *
 * A Pane or Card only adds a level when it has a title: the title registers
 * itself (`useRegisterTitle`), so an untitled card around an AI answer, or a
 * pane named by `aria-label`, leaves its content at the outer level.
 *
 * Every such part also takes `headingLevel`, which wins. Outside any provider
 * a part keeps its own default (CardTitle h3, PaneHeader h2, …), so a part on
 * its own in a story or test is unchanged.
 */

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingTag = `h${HeadingLevel}`;

const HeadingLevelContext = React.createContext<HeadingLevel | undefined>(undefined);

/** The explicit level, else the one from context, else the part's own default. */
export function useHeadingLevel(explicit: HeadingLevel | undefined, fallback: HeadingLevel): HeadingLevel {
  const context = React.useContext(HeadingLevelContext);
  return explicit ?? context ?? fallback;
}

/** One level deeper, capped at 6. */
export function deeper(level: HeadingLevel): HeadingLevel {
  return Math.min(level + 1, 6) as HeadingLevel;
}

export function headingTag(level: HeadingLevel): HeadingTag {
  return `h${level}`;
}

/**
 * A container (Pane, Card) whose content goes one level deeper only while its
 * title is mounted. The title calls `register` from a layout effect, so the
 * levels settle before the first paint.
 */
export function useTitledLevel(level: HeadingLevel) {
  const [titled, setTitled] = React.useState(0);
  const register = React.useCallback(() => {
    setTitled((n) => n + 1);
    return () => setTitled((n) => n - 1);
  }, []);
  return { contentLevel: titled > 0 ? deeper(level) : level, register };
}

/** For a title: tell its container it has one. */
export function useRegisterTitle(register: (() => () => void) | undefined) {
  React.useLayoutEffect(() => register?.(), [register]);
}

/** Headings inside start at `level`. */
export function HeadingLevelProvider({ level, children }: { level: HeadingLevel; children: React.ReactNode }) {
  return <HeadingLevelContext.Provider value={level}>{children}</HeadingLevelContext.Provider>;
}
